import React, { memo, useMemo } from 'react';
import { DatePicker } from '@material-ui/pickers';
import { IconButton, Typography } from '@material-ui/core';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import ClearIcon from '@material-ui/icons/Clear';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { dateTodayVanilla } from 'src/utils';

interface Props {
  title?: string;
  fromDateValue?: MaterialUiPickersDate;
  toDateValue?: MaterialUiPickersDate;
  fullwidth?: boolean;

  onChangeFromDate: (date: MaterialUiPickersDate) => void;
  onChangeToDate: (date: MaterialUiPickersDate) => void;
}

const QuarterDatePicker = ({
  title,
  fromDateValue,
  toDateValue,
  fullwidth = false,

  onChangeFromDate,
  onChangeToDate
}: Props) => {
  const maxToDate = useMemo(() => {
    if (!fromDateValue) return undefined;

    const month = fromDateValue.getMonth();
    const year = fromDateValue.getFullYear();

    // Quarter end month index (0-based)
    const quarterEndMonth = Math.floor(month / 3) * 3 + 2;

    // Return the last day of that quarter
    return new Date(year, quarterEndMonth + 1, 0);
  }, [fromDateValue]);

  const onClearPress = (
    e: React.MouseEvent<HTMLButtonElement>,
    item: 'to' | 'from'
  ) => {
    e.stopPropagation();
    if (item === 'from') {
      onChangeFromDate(null);
    }
    if (item === 'to') {
      onChangeToDate(null);
    }
  };

  return (
    <div style={{ width: fullwidth ? '100%' : 'auto' }}>
      {title ? (
        <Typography align="center" variant="h5" style={{ marginBottom: 8 }}>
          {title}
        </Typography>
      ) : null}

      <div
        style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
      >
        <DatePicker
          autoOk
          views={['month']}
          label="From"
          maxDate={dateTodayVanilla()}
          value={fromDateValue}
          inputVariant="outlined"
          onChange={(newValue) => {
            if (!newValue) return onChangeFromDate(null);
            const firstDay = new Date(
              Date.UTC(newValue.getFullYear(), newValue.getMonth(), 1)
            );
            const lastDay = new Date(
              Date.UTC(newValue.getFullYear(), newValue.getMonth() + 1, 0)
            );

            onChangeFromDate(firstDay);
            onChangeToDate(lastDay);
          }}
          InputProps={{
            endAdornment: (
              <IconButton size="small" onClick={(e) => onClearPress(e, 'from')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            )
          }}
        />
        <ArrowForwardIcon style={{ marginLeft: 3, marginRight: 3 }} />
        <DatePicker
          autoOk
          inputVariant="outlined"
          views={['month']}
          label="To"
          value={toDateValue}
          minDate={fromDateValue}
          maxDate={maxToDate}
          onChange={(newValue) => {
            if (!newValue) return onChangeToDate(null);
            const lastDay = new Date(
              Date.UTC(newValue.getFullYear(), newValue.getMonth() + 1, 0)
            );
            onChangeToDate(lastDay);
          }}
          InputProps={{
            endAdornment: (
              <IconButton size="small" onClick={(e) => onClearPress(e, 'to')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            )
          }}
          disabled={!fromDateValue}
        />
      </div>
    </div>
  );
};

export default memo(QuarterDatePicker);
